import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import {
  SegmentedTabBar,
  SEGMENTED_TAB_BAR_EVENT,
  type TabBarStyleToken,
} from "@yjsgames/phaser-ui";
import { SingleSelection } from "@yjsgames/single-select";
import type { TextDirection } from "@yjsgames/i18n";
import type { MaterialButtonState } from "../systems/ModuleMerge.js";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import { PALETTE } from "../themes/palette.js";
import { t } from "../i18n/i18n.js";
import {
  ModuleMergeTabView,
  type ModuleMergeTabData,
  type ModuleMergeTabViewOptions,
} from "./ModuleMergeTabView.js";
import {
  ModuleFusionTabView,
  type ModuleFusionTabData,
  type ModuleFusionTabViewOptions,
} from "./ModuleFusionTabView.js";

/** 모듈 탭 내 서브탭 — 합성(merge) / 융합(fusion). */
type SubTabId = "merge" | "fusion";

/** 서브탭 바 높이 + 콘텐츠와의 간격 (ShopContainerTabView 동형). 콘텐츠를 서브탭 바에 바짝 붙여 영역 확보(사용자 요청). */
const SUB_BAR_HEIGHT = 30;
const SUB_BAR_GAP = 2;

/** 서브탭 바 스타일 — 하단 탭바와 동일 9-slice 에셋, caption 폰트 (ShopContainerTabView 동형). */
const SUB_TAB_BAR_STYLE: TabBarStyleToken = {
  activeTextureKey: "tab_button_active_9slice",
  inactiveTextureKey: "tab_button_inactive_9slice",
  border: { left: 10, top: 10, right: 10, bottom: 10 },
  labelStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary },
  labelActiveStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textBright },
  inactiveAlpha: 0.65,
  activeLabelScale: 1.04,
};

function iconTab(activeIconKey: string): { iconKey: string; activeIconKey: string; inactiveIconKey: string } {
  return { iconKey: activeIconKey, activeIconKey, inactiveIconKey: `${activeIconKey}_inactive` };
}

export interface ModuleComposeContainerOptions {
  readonly merge: ModuleMergeTabViewOptions;
  readonly fusion: ModuleFusionTabViewOptions;
  /** 융합 서브탭 활성 여부(featureFlags.moduleFusion). false면 합성만 표시(서브탭 바 없음). */
  readonly fusionEnabled: boolean;
  /** 융합 시스템 해금 여부. feature flag와 별개로 진행도 기반 잠금에 사용한다. */
  readonly isFusionUnlocked?: () => boolean;
  /** 잠긴 융합 서브탭을 누르면 호출한다. */
  readonly onFusionLocked?: () => void;
  /** 서브탭 전환 시 호출 — GameScene이 활성 서브탭 데이터를 갱신한다. */
  readonly onSubChange: () => void;
}

/**
 * 모듈 컨테이너 탭 — 하단 서브탭 바([합성]/[융합], 엄지 친화) + 활성 서브탭 콘텐츠 1개를 표시한다.
 * 기존 단독 최상위 탭이던 moduleMerge를 합성/융합 두 서브탭으로 나눈다(사용자 요청). 가시성은 SingleSelection SSoT.
 * ShopContainerTabView와 동형 — featureFlags.moduleFusion=false(웹 live)면 서브탭 바 없이 ModuleMergeTabView만 풀 영역.
 */
export class ModuleComposeContainerTabView implements TabViewContract {
  private readonly subBar?: SegmentedTabBar<SubTabId>;
  private readonly mergeView: ModuleMergeTabView;
  private readonly fusionView?: ModuleFusionTabView;
  private readonly selection: SingleSelection<SubTabId>;
  private readonly onSubChange: () => void;
  private readonly opts: ModuleComposeContainerOptions;
  /** 컨테이너 탭 자체 표시 상태 — 자식 가시성은 (this.visible && 서브탭 활성)으로 게이트. */
  private visible = true;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    bottomY: number,
    opts: ModuleComposeContainerOptions,
  ) {
    this.opts = opts;
    this.onSubChange = opts.onSubChange;
    const hasFusion = opts.fusionEnabled;
    // 서브탭 바를 콘텐츠 하단에 배치(엄지 친화) — 콘텐츠는 상단(y)부터, 하한을 서브탭 바 위로 좁힌다.
    const subBarY = bottomY - SUB_BAR_HEIGHT;
    const contentBottomY = hasFusion ? subBarY - SUB_BAR_GAP : bottomY;

    this.mergeView = new ModuleMergeTabView(scene, x, y, width, contentBottomY, opts.merge);
    if (hasFusion) {
      this.fusionView = new ModuleFusionTabView(scene, x, y, width, contentBottomY, opts.fusion);
      this.subBar = new SegmentedTabBar<SubTabId>(scene, x, subBarY, {
        style: SUB_TAB_BAR_STYLE,
        width,
        height: SUB_BAR_HEIGHT,
        tabs: [
          { id: "merge", label: t("subtab.merge"), ...iconTab("icon_subtab_merge") },
          { id: "fusion", label: t("subtab.fusion"), ...iconTab("icon_subtab_fusion") },
        ],
        activeId: "merge",
      });
      this.subBar.setDepth(3);
      this.subBar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: SubTabId) => this.selectSub(id));
      scene.add.existing(this.subBar);
    }
    this.selection = new SingleSelection<SubTabId>(
      hasFusion ? ["merge", "fusion"] : ["merge"],
      "merge",
    );
    this.applySubVisibility();
  }

  private selectSub(id: SubTabId): void {
    if (id === "fusion" && !this.fusionUnlocked()) {
      this.opts.onFusionLocked?.();
      return;
    }
    if (!this.selection.select(id)) return;
    this.subBar?.setActiveTab(id);
    this.applySubVisibility();
    this.onSubChange();
  }

  /** 서브탭 가시성을 selection SSoT에서 파생 — 컨테이너가 보일 때만 활성 서브탭 1개를 표시. */
  private applySubVisibility(): void {
    const vis = this.selection.visibility();
    this.mergeView.setVisible(this.visible && vis.merge);
    this.fusionView?.setVisible(this.visible && this.fusionUnlocked() && (vis.fusion ?? false));
  }

  private fusionUnlocked(): boolean {
    return this.opts.isFusionUnlocked?.() ?? true;
  }

  /** 현재 활성 서브탭 — GameScene이 어느 데이터(합성/융합)를 갱신할지 결정. */
  activeSub(): SubTabId {
    return this.selection.active;
  }

  updateMerge(d: ModuleMergeTabData): void {
    this.mergeView.update(d);
  }

  markMergeResult(instanceId: string): void {
    this.mergeView.markMergeResult(instanceId);
  }

  updateFusion(d: ModuleFusionTabData): void {
    if (!this.fusionUnlocked()) {
      if (this.selection.active === "fusion") this.selectSub("merge");
      this.applySubVisibility();
      return;
    }
    this.fusionView?.update(d);
  }

  /** 합성 셀 정보 팝업 등록 버튼 상태 — openMergeModuleInfo가 사용. */
  mergeButtonState(id: string): MaterialButtonState {
    return this.mergeView.materialButtonState(id);
  }
  /** 합성 재료 토글 (정보 팝업 등록/해제). */
  toggleMergeMaterial(id: string): void {
    this.mergeView.toggleMaterial(id);
  }
  /** 융합 셀 정보 팝업 등록 버튼 상태 — openFusionModuleInfo가 사용. 융합 미활성 시 disabled. */
  fusionButtonState(id: string): MaterialButtonState {
    return this.fusionView?.materialButtonState(id) ?? { action: "register", enabled: false };
  }
  /** 융합 재료 토글 (정보 팝업 등록/해제). */
  toggleFusionMaterial(id: string): void {
    this.fusionView?.toggleMaterial(id);
  }

  applyLanguage(): void {
    this.subBar?.setTabLabels((id) => (id === "merge" ? t("subtab.merge") : t("subtab.fusion")));
    this.mergeView.applyLanguage();
    this.fusionView?.applyLanguage();
  }

  /** 방향(LTR↔RTL) 전환 — 서브탭 바 + 합성/융합 콘텐츠에 전파. */
  relayout(dir: TextDirection): void {
    this.subBar?.relayout(dir);
    this.mergeView.relayout(dir);
    this.fusionView?.relayout(dir);
  }

  /**
   * 셸 펼치기/접기 — 새 콘텐츠 rect를 두 서브뷰에 전달(리스트가 확장 패널을 채우게).
   * ⚠ 서브탭 바는 패널 하단 고정(펼침=패널 위로 확장, 하단 불변)이라 위치 불변 — 자식 rect는 그 위까지로 좁힌다.
   */
  resize(rect: TabContentRect): void {
    const subReserve = this.fusionView ? SUB_BAR_HEIGHT + SUB_BAR_GAP : 0;
    const childRect = { x: rect.x, y: rect.y, width: rect.width, height: rect.height - subReserve };
    this.mergeView.resize(childRect);
    this.fusionView?.resize(childRect);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.subBar?.setVisible(visible);
    this.applySubVisibility();
  }
}
